import { assertErrorOrThrowUnknown } from './errorUtils'

/**
 * Authentication error codes for OAuth and Okta error handling.
 *
 * Provides standardized error codes for authentication failures, including
 * OAuth 2.0 standard errors and Okta-specific error types used throughout
 * the BOSS UI authentication flow.
 */
export enum AuthenticationErrorCode {
	AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
	UNKNOWN = 'UNKNOWN',
	OAUTHCALLBACKERROR = 'OAuthCallbackError',
	OAUTHSTATEERROR = 'OAuthStateError',
	OAUTHRESPONSEERROR = 'OAuthResponseError',
	OAUTHHTTPERROR = 'OAuthHTTPError',
	INTERACTIONREQUIRED = 'InteractionRequired',
	ACCESS_DENIED = 'access_denied',
	INVALID_GRANT = 'invalid_grant',
	CONSENT_REQUIRED = 'consent_required',
	INVALID_REQUEST = 'invalid_request',
	INVALID_CLIENT = 'invalid_client',
	SERVER_ERROR = 'server_error',
}

/**
 * Standard OAuth 2.0 error response structure from Okta.
 *
 * Represents the error response format returned by Okta's authentication
 * endpoints according to OAuth 2.0 specifications and Okta's error code
 * documentation: https://developer.okta.com/docs/reference/error-codes/
 *
 * @property errorCode - Okta-specific code identifying the error type
 * @property errorSummary - Human-readable description of the error cause
 * @property errorLink - Okta code reference for documentation lookup
 * @property errorId - Unique identifier for troubleshooting with Okta Support
 * @property errorCauses - Optional additional information about the error
 */
export type OktaErrorResponse = {
	errorCode: string // An Okta code for this type of error
	errorSummary: string // A short description of what caused this error. Sometimes this contains dynamically-generated information about your specific error.
	errorLink: string // An Okta code for this type of error
	errorId: string // A unique identifier for this error. This can be used by Okta Support to help with troubleshooting.
	errorCauses?: string // (Optional) Further information about what caused this error
}

/**
 * Type guard to check if a value is an Okta error response.
 *
 * Uses both Object.hasOwn and the `in` operator to support various object
 * shapes, including plain objects and class instances with error properties.
 * Validates presence of either errorId or errorLink properties.
 *
 * @param value - Value to check
 * @returns True if value is a valid OktaErrorResponse
 */
export function isOktaError(value: unknown): value is OktaErrorResponse {
	if (!value || typeof value !== 'object') return false
	// Prefer Object.hasOwn when available, fallback to the `in` operator for older shapes
	const hasErrorId = Object.hasOwn(value, 'errorId') || 'errorId' in value
	const hasErrorLink = Object.hasOwn(value, 'errorLink') || 'errorLink' in value
	return hasErrorId || hasErrorLink
}

/**
 * Custom error class for authentication failures.
 *
 * Extends the native Error class to include structured Okta error details.
 * Provides access to OAuth 2.0 error information for proper error handling
 * and user feedback in authentication flows.
 */
export class AuthenticationError extends Error {
	public readonly details: OktaErrorResponse

	/**
	 * Creates an AuthenticationError instance from Okta error details.
	 *
	 * @param details - Structured error response from Okta authentication endpoint
	 */
	constructor(details: OktaErrorResponse) {
		// Prefer the most descriptive id available for the message
		super(details.errorCode ?? details.errorLink ?? 'AuthenticationError')
		Object.setPrototypeOf(this, AuthenticationError.prototype)
		this.name = 'AuthenticationError'
		this.details = details
	}
}

/**
 * Handles authentication errors by normalizing and re-throwing them.
 *
 * Converts Okta-specific error responses into AuthenticationError instances
 * for consistent error handling. Falls back to standard error handling for
 * non-Okta errors.
 *
 * @param error - Error value to handle (can be any type)
 * @param errorText - Additional error context text
 * @throws Always throws either AuthenticationError or normalized Error
 */
export function handleAuthenticationError(
	error: unknown,
	errorText?: string
): void {
	// If it's an Okta-style error, wrap and throw a typed AuthenticationError
	if (isOktaError(error)) {
		throw new AuthenticationError(error)
	}

	// Standard Error instance handling — delegate to shared util which always throws
	assertErrorOrThrowUnknown(
		error,
		errorText,
		undefined,
		AuthenticationErrorCode.UNKNOWN
	)
}
